import { useState } from 'react';
import { motion } from 'framer-motion';
import { CheckIcon, XMarkIcon, StarIcon } from '@heroicons/react/24/outline';
import type { SubscriptionTierInfo, BillingPeriod } from '@types';
import { formatEmployeeLimit } from '@/types/subscription';

interface TierComparisonProps {
  tiers: SubscriptionTierInfo[];
  currentTier: string;
  onSelectTier: (tier: string) => void;
}

const tierGradients = {
  FREE: 'from-gray-400 to-gray-600',
  BASIC: 'from-blue-400 to-blue-600',
  PROFESSIONAL: 'from-purple-400 to-purple-600',
  ENTERPRISE: 'from-indigo-500 to-purple-600',
};

export default function TierComparison({
  tiers,
  currentTier,
  onSelectTier,
}: TierComparisonProps) {
  const [billingPeriod, setBillingPeriod] = useState<BillingPeriod>('MONTHLY');

  // Sort tiers by hierarchy
  const tierOrder = ['FREE', 'BASIC', 'PROFESSIONAL', 'ENTERPRISE'];
  const sortedTiers = [...tiers].sort(
    (a, b) => tierOrder.indexOf(a.tier) - tierOrder.indexOf(b.tier)
  );

  const getButtonText = (tier: string) => {
    if (tier === currentTier) return 'Current Plan';
    const currentIndex = tierOrder.indexOf(currentTier);
    const tierIndex = tierOrder.indexOf(tier);
    if (tierIndex > currentIndex) return 'Upgrade';
    return 'Select Plan';
  };

  const isCurrentTier = (tier: string) => tier === currentTier;
  const canSelect = (tier: string) => tier !== currentTier;

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: 0.2 }}
      className="bg-white overflow-hidden shadow rounded-lg"
    >
      <div className="px-4 py-5 sm:p-6">
        <div className="flex items-center justify-between mb-6">
          <h3 className="text-lg leading-6 font-medium text-gray-900">
            Compare Plans
          </h3>

          {/* Billing Period Toggle */}
          <div className="flex items-center space-x-2 bg-gray-100 rounded-lg p-1">
            <button
              onClick={() => setBillingPeriod('MONTHLY')}
              className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
                billingPeriod === 'MONTHLY'
                  ? 'bg-white text-gray-900 shadow-sm'
                  : 'text-gray-500 hover:text-gray-700'
              }`}
            >
              Monthly
            </button>
            <button
              onClick={() => setBillingPeriod('YEARLY')}
              className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
                billingPeriod === 'YEARLY'
                  ? 'bg-white text-gray-900 shadow-sm'
                  : 'text-gray-500 hover:text-gray-700'
              }`}
            >
              Yearly
            </button>
          </div>
        </div>

        {/* Tiers Grid */}
        <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-4">
          {sortedTiers.map((tier, index) => {
            const gradient =
              tierGradients[tier.tier as keyof typeof tierGradients] ||
              tierGradients.FREE;
            const isCurrent = isCurrentTier(tier.tier);
            const supportsBillingPeriod =
              tier.billing_periods.includes(billingPeriod);

            return (
              <motion.div
                key={tier.tier}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.1 }}
                className={`relative rounded-lg border-2 ${
                  isCurrent
                    ? 'border-indigo-600 shadow-lg ring-2 ring-indigo-600'
                    : 'border-gray-200 hover:border-indigo-300'
                } overflow-hidden`}
              >
                {/* Tier Header */}
                <div
                  className={`bg-gradient-to-r ${gradient} px-6 py-4 text-white`}
                >
                  <div className="flex items-center justify-between">
                    <h4 className="text-lg font-bold">{tier.tier}</h4>
                    {isCurrent && <StarIcon className="h-5 w-5" />}
                  </div>
                  <div className="mt-2">
                    <span className="text-3xl font-bold">${tier.price}</span>
                    <span className="text-sm opacity-90">
                      /{billingPeriod === 'MONTHLY' ? 'mo' : 'yr'}
                    </span>
                  </div>
                  {billingPeriod === 'YEARLY' && tier.tier !== 'FREE' && (
                    <p className="text-xs mt-1 opacity-90">Save 20% annually</p>
                  )}
                </div>

                {/* Tier Body */}
                <div className="px-6 py-4">
                  {/* Employee Limit */}
                  <div className="mb-4 pb-4 border-b border-gray-200">
                    <p className="text-sm text-gray-500">Employee Limit</p>
                    <p className="text-2xl font-bold text-gray-900">
                      {formatEmployeeLimit(tier.employee_limit)}
                    </p>
                  </div>

                  {/* Features List */}
                  <ul className="space-y-3 mb-6">
                    {tier.features.map((feature, idx) => {
                      const isIncluded = !feature.startsWith('No ');
                      const displayFeature = isIncluded
                        ? feature
                        : feature.substring(3);

                      return (
                        <li key={idx} className="flex items-start">
                          {isIncluded ? (
                            <CheckIcon className="h-5 w-5 text-green-500 mr-2 flex-shrink-0" />
                          ) : (
                            <XMarkIcon className="h-5 w-5 text-gray-300 mr-2 flex-shrink-0" />
                          )}
                          <span
                            className={`text-sm ${
                              isIncluded
                                ? 'text-gray-700'
                                : 'text-gray-400 line-through'
                            }`}
                          >
                            {displayFeature}
                          </span>
                        </li>
                      );
                    })}
                  </ul>

                  {/* Action Button */}
                  <button
                    onClick={() =>
                      canSelect(tier.tier) && onSelectTier(tier.tier)
                    }
                    disabled={!canSelect(tier.tier) || !supportsBillingPeriod}
                    className={`w-full py-2 px-4 rounded-md font-medium transition-colors ${
                      isCurrent
                        ? 'bg-indigo-100 text-indigo-700 cursor-default'
                        : !supportsBillingPeriod
                          ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                          : 'bg-indigo-600 text-white hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500'
                    }`}
                  >
                    {!supportsBillingPeriod
                      ? 'Not Available'
                      : getButtonText(tier.tier)}
                  </button>
                </div>

                {/* Current Plan Badge */}
                {isCurrent && (
                  <div className="absolute top-0 right-0 -mt-1 -mr-1">
                    <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-indigo-600 text-white shadow-lg">
                      Current
                    </span>
                  </div>
                )}
              </motion.div>
            );
          })}
        </div>

        {/* Note */}
        <div className="mt-6 text-center">
          <p className="text-sm text-gray-500">
            All plans include basic support. Enterprise includes dedicated
            account manager.
          </p>
        </div>
      </div>
    </motion.div>
  );
}
